import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/rental-manager-env';
import { emitDownloadCSVEvent } from '../model/ClickstreamEvents';
import billingUtils from './billingUtils';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraBillingInvoiceItemDto,
} from '@zg-rentals/ts-adproducts-client';

const SLICE_SIZE = 512;

const safePrintCsvValue = (value?: string) => {
  return value ? value : '';
};

export const downloadRocketshipCsv = (
  currentInvoice: AdproductsZuoraBillingInvoiceDto,
  user: RentalManagerAuthCheckUserInfoWebApiDto,
) => {
  const { sfhLineItems } = currentInvoice;
  const hasDiscount = sfhLineItems.some((item: AdproductsZuoraBillingInvoiceItemDto) => Boolean(item.discountPercent));
  const hasMlsName = sfhLineItems.some((item: AdproductsZuoraBillingInvoiceItemDto) => Boolean(item.mlsName));
  const hasMlsListingId = sfhLineItems.some((item: AdproductsZuoraBillingInvoiceItemDto) => Boolean(item.mlsListingId));
  const isFlatFeeFeedConnect = billingUtils.isFlatFeeFeedConnectInvoice(sfhLineItems);
  let data = 'Listing Source, ListingId, Contact Email, Contact Phone,';
  data += 'Property address, Unit, City, State,';
  data += isFlatFeeFeedConnect ? 'Date Ranges Active,' : 'Billable Date Ranges Active,';
  if (!isFlatFeeFeedConnect) {
    data += 'Days Billed,';
    data += hasDiscount ? 'Discounted Daily Rate,' : 'Amount Per Day,';
  }
  if (hasDiscount && !isFlatFeeFeedConnect) {
    data += 'Market Daily Rate,';
  }
  data += isFlatFeeFeedConnect ? 'Estimated Amount,' : 'Total Amount,';
  data += 'Feed Company';
  if (hasMlsListingId) {
    data += ',Mls Listing Id';
  }
  if (hasMlsName) {
    data += ',Mls name,';
  }
  data += '\n';
  sfhLineItems.forEach((item: AdproductsZuoraBillingInvoiceItemDto) => {
    const {
      address = '',
      city,
      state,
      quantity,
      chargeAmount,
      unit = '',
      activeRanges,
      feedId,
      contactEmail,
      contactPhone,
      discountPercent,
      feedCompanyName,
      mlsListingId,
      mlsName,
      feedListingId,
    } = item;
    const rate = chargeAmount / quantity;
    const discount = (100 - discountPercent) / 100;
    const originalRate = rate / discount;
    const formattedStreet = address.split(',')[0];
    const formattedUnit = unit.replace(/,/g, '');
    const listingSource = feedId === 'HotPads' || feedId === 'Postlets' ? 'Zillow' : feedId;
    data += `${listingSource},${feedListingId},${contactEmail},${contactPhone},`;
    data += `${formattedStreet},${formattedUnit},${city},${state},`;
    data += `${activeRanges.replaceAll(',', ';')},`;
    if (!isFlatFeeFeedConnect) {
      data += ` ${quantity},${rate},`;
    }
    if (hasDiscount && !isFlatFeeFeedConnect) {
      data += `${Math.round(originalRate * 100) / 100},`;
    }
    data += `${chargeAmount},${safePrintCsvValue(feedCompanyName)}`;
    if (hasMlsListingId) {
      data += ',' + safePrintCsvValue(mlsListingId);
    }
    if (hasMlsName) {
      data += ',' + safePrintCsvValue(mlsName);
    }
    data += '\n';
  });
  downloadBase64Data(btoa(data), 'application/octet-stream', `${currentInvoice.invoiceNumber}.csv`);
  emitDownloadCSVEvent({ user });
};

export const downloadSingleFamilyLegacyCsv = (
  currentInvoice: AdproductsZuoraBillingInvoiceDto,
  user: RentalManagerAuthCheckUserInfoWebApiDto,
) => {
  let data = 'Property address, Unit, City, State,';
  data += 'Active Days, Est. Amount, Feed Company\n';
  currentInvoice.sfhLineItems.forEach((item: AdproductsZuoraBillingInvoiceItemDto) => {
    const { address = '', city, state, quantity, chargeAmount, unit = '', feedCompanyName } = item;
    const formattedStreet = address.split(',')[0];
    const formattedUnit = unit.replace(/,/g, '');
    data += `${formattedStreet},${formattedUnit},${city},${state},`;
    data += `${quantity},${chargeAmount},${feedCompanyName || ''}\n`;
  });
  downloadBase64Data(btoa(data), 'application/octet-stream', `${currentInvoice.invoiceNumber}_estimate.csv`);
  emitDownloadCSVEvent({ user });
};

const b64toBlob = (b64Data: string, contentType: string, sliceSize: number) => {
  contentType = contentType || '';
  const byteCharacters = atob(b64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(byteNumbers);
  }
  return new Blob(byteArrays, { type: contentType });
};

const downloadBase64Data = (data: string, contentType: string, fileName: string) => {
  const blob = b64toBlob(data, contentType, SLICE_SIZE);
  const downloadLink = document.createElement('a');
  downloadLink.href = window.URL.createObjectURL(blob);
  downloadLink.setAttribute('download', fileName);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.URL.revokeObjectURL(downloadLink.href);
};

export { downloadBase64Data };
